import { useDispatch, useSelector } from "react-redux";
import { selectProducts } from "../../../store/selectors/ProductSelector";
import "./Product.css";
import { useTranslation } from "react-i18next";
import { useDisclosure, useToast } from "@chakra-ui/react";
import {
  FiEdit2,
  FiTrash2,
  FiInfo,
  FiDownload,
  FiSearch,
  FiX,
  FiPlus,
  FiPackage,
  FiFacebook,
  FiImage,
} from "react-icons/fi";
import { CreateAndUpdateProduct } from "./CreateAndUpdateProduct";
import { Product } from "../../../entity/Product";
import { WSProduct } from "../../../services/remote/product/WSProduct";
import { useMemo, useState } from "react";
import {
  setProducts,
  removeProduct,
  updateProduct as updateProductInStore,
} from "../../../store/slices/ProductSlice";
import Dialog from "../../components/Dialog";
import { Link } from "react-router-dom";
import { COLORS } from "../../../utils/Colors";
import { IconButton } from "../../components/buttons/IconButton";
import { generateMetaDynamic } from "../../../helpers/GenerateMetaDynamic";
import { selectStore } from "../../../store/selectors/StoreSelector";
import { ExportManager } from "../../../helpers/exportManager/ExportManager";
import { CONSTANTS } from "../../../utils/Constants";

type StockFilter = "all" | "in" | "low" | "out";
type SortKey = "newest" | "name" | "price_asc" | "price_desc" | "quantity";

const LOW_STOCK_THRESHOLD = 5;

const getStockStatus = (quantity: number): Exclude<StockFilter, "all"> => {
  if (quantity <= 0) return "out";
  if (quantity <= LOW_STOCK_THRESHOLD) return "low";
  return "in";
};

export const ProductScreen = () => {
  const products = useSelector(selectProducts);
  const store = useSelector(selectStore);

  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productId, setProductId] = useState<string>("");
  const [openDialoge, setOpenDialoge] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");

  const dispatch = useDispatch();
  const instance = WSProduct.getInstance();

  const showToast = (title: string, status: "success" | "error") =>
    toast({ title, status, duration: 3000, isClosable: true });

  /* ---------- derived data ---------- */

  const stats = useMemo(() => {
    const total = products.length;
    const out = products.filter((p) => p.quantity <= 0).length;
    const low = products.filter(
      (p) => p.quantity > 0 && p.quantity <= LOW_STOCK_THRESHOLD,
    ).length;
    const value = products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    return { total, out, low, value };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const list = products.filter((p) => {
      const matchesSearch = !term || p.name.toLowerCase().includes(term);
      const matchesStock =
        stockFilter === "all" || getStockStatus(p.quantity) === stockFilter;
      return matchesSearch && matchesStock;
    });

    switch (sortKey) {
      case "name":
        return [...list].sort((a, b) => a.name.localeCompare(b.name));
      case "price_asc":
        return [...list].sort((a, b) => a.price - b.price);
      case "price_desc":
        return [...list].sort((a, b) => b.price - a.price);
      case "quantity":
        return [...list].sort((a, b) => a.quantity - b.quantity);
      default:
        return list;
    }
  }, [products, searchTerm, stockFilter, sortKey]);

  /* ---------- handlers ---------- */

  const handleCreateProduct = async (product: Product) => {
    try {
      const result = await instance.createProduct(product);
      if (result) {
        dispatch(setProducts([{ ...product, id: result }, ...products]));
        onClose();
        showToast("Product created", "success");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      showToast("Error creating product", "error");
    }
  };

  const handleUpdateProduct = async (product: Product) => {
    try {
      await instance.updateProduct(product);
      dispatch(updateProductInStore(product));
      setEditingProduct(null);
      showToast("Product updated", "success");
    } catch (error) {
      console.error("Error updating product:", error);
      showToast("Error updating product", "error");
    }
  };

  const handleDeleteProductClick = (id: string) => {
    setProductId(id);
    setOpenDialoge(true);
  };

  const handleDeleteProduct = async () => {
    try {
      await instance.deleteProduct(productId);
      dispatch(removeProduct(productId));
      showToast(t("popup.txt_delete_product"), "success");
    } catch (error) {
      console.error("Error deleting product:", error);
      showToast(t("popup.txt_error_deleting_product"), "error");
    } finally {
      setOpenDialoge(false);
    }
  };

  const handleCopyMetaDynamicLink = (id: string) => {
    const url = generateMetaDynamic(id, store!);
    navigator.clipboard.writeText(url);
    showToast("Meta dynamic link copied", "success");
  };

  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    onOpen();
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStockFilter("all");
    setSortKey("newest");
  };

  const hasActiveFilters =
    searchTerm !== "" || stockFilter !== "all" || sortKey !== "newest";

  const filters: { key: StockFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: stats.total },
    {
      key: "in",
      label: "In stock",
      count: stats.total - stats.low - stats.out,
    },
    { key: "low", label: "Low stock", count: stats.low },
    { key: "out", label: "Out of stock", count: stats.out },
  ];

  /* ---------- render ---------- */

  return (
    <div className="product-container">
      {/* Header */}
      <div className="product-header">
        <div className="product-heading">
          <h2 className="product-title">{t("sidebar.products")}</h2>
          <span className="product-count">{stats.total}</span>
        </div>

        <div className="product-actions">
          <button
            className="product-secondary-button"
            onClick={() =>
              ExportManager.exportCSV(
                filteredProducts,
                CONSTANTS.EXPORTED_PRODUCT_CSV_FILE_NAME,
              )
            }
          >
            <FiDownload size={15} />
            {t("buttons.export_csv")}
          </button>

          <button
            className="product-secondary-button"
            onClick={() =>
              ExportManager.exportPDF(
                filteredProducts.map((product) => ({
                  name: product.name,
                  price: product.price,
                  stock: product.inStock,
                  quantity: product.quantity,
                  createdAt: product.createdAt?.toLocaleDateString() || "",
                })),
                CONSTANTS.EXPORTED_PRODUCT_PDF_FILE_NAME,
              )
            }
          >
            <FiDownload size={15} />
            {t("buttons.export_pdf")}
          </button>

          <button className="product-add-product-button" onClick={onOpen}>
            <FiPlus size={16} />
            {t("buttons.add_product")}
          </button>
        </div>
      </div>

      <CreateAndUpdateProduct
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setEditingProduct(null);
        }}
        onCreate={handleCreateProduct}
        onUpdate={handleUpdateProduct}
        product={editingProduct}
      />

      {/* Stats */}
      <div className="product-stats">
        <div className="product-stat-card">
          <span className="product-stat-label">Total products</span>
          <span className="product-stat-value">{stats.total}</span>
        </div>
        <div className="product-stat-card product-stat-warning">
          <span className="product-stat-label">Low stock</span>
          <span className="product-stat-value">{stats.low}</span>
        </div>
        <div className="product-stat-card product-stat-danger">
          <span className="product-stat-label">Out of stock</span>
          <span className="product-stat-value">{stats.out}</span>
        </div>
        <div className="product-stat-card product-stat-success">
          <span className="product-stat-label">Inventory value</span>
          <span className="product-stat-value">
            $
            {stats.value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      {/* Table card */}
      <div className="product-table-container">
        <div className="product-toolbar">
          <div className="product-search-container">
            <FiSearch
              size={16}
              color={COLORS.Neutral.Gray}
              className="product-search-icon"
            />
            <input
              type="text"
              value={searchTerm}
              placeholder={t("buttons.search_product")}
              className="product-search-input"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="product-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          <div className="product-filter-chips" role="tablist">
            {filters.map((f) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={stockFilter === f.key}
                className={`product-filter-chip ${
                  stockFilter === f.key ? "product-filter-chip-active" : ""
                }`}
                onClick={() => setStockFilter(f.key)}
              >
                {f.label}
                <span className="product-filter-chip-count">{f.count}</span>
              </button>
            ))}
          </div>

          <select
            className="product-sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label="Sort products"
          >
            <option value="newest">Newest first</option>
            <option value="name">Name (A–Z)</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="quantity">Quantity: low to high</option>
          </select>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="product-empty">
            <div className="product-empty-icon">
              <FiPackage size={28} />
            </div>
            <h3>
              {hasActiveFilters ? "No matching products" : "No products yet"}
            </h3>
            <p>
              {hasActiveFilters
                ? "Try adjusting your search or filters."
                : "Add your first product to start selling."}
            </p>
            {hasActiveFilters ? (
              <button
                className="product-secondary-button"
                onClick={resetFilters}
              >
                Reset filters
              </button>
            ) : (
              <button className="product-add-product-button" onClick={onOpen}>
                <FiPlus size={16} />
                {t("buttons.add_product")}
              </button>
            )}
          </div>
        ) : (
          <table className="product-table">
            <thead>
              <tr>
                <th></th>
                <th>{t("buttons.txt_product")}</th>
                <th>{t("buttons.txt_quantity")}</th>
                <th>{t("buttons.txt_price")}</th>
                <th>{t("buttons.txt_in_stock")}</th>
                <th>{t("buttons.txt_meta_dynamic")}</th>
                <th>{t("buttons.txt_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const status = getStockStatus(product.quantity);
                const statusLabel = {
                  in: "In Stock",
                  low: "Low Stock",
                  out: "Out of Stock",
                }[status];

                return (
                  <tr key={product.id}>
                    <td className="product-img" data-label="">
                      {product.imageUrl && product.imageUrl.length > 0 ? (
                        <img
                          src={product.imageUrl[0]}
                          alt={product.name}
                          loading="lazy"
                        />
                      ) : (
                        <div className="product-img-placeholder">
                          <FiImage size={20} />
                        </div>
                      )}
                    </td>

                    <td
                      className="product-name-cell"
                      data-label={t("buttons.txt_product")}
                    >
                      <Link to={`/dashboard/products/${product.id}`}>
                        {product.name}
                      </Link>
                    </td>

                    <td data-label={t("buttons.txt_quantity")}>
                      <span className={`product-type-badge qty-${status}`}>
                        {product.quantity}
                      </span>
                    </td>

                    <td
                      className="product-price"
                      data-label={t("buttons.txt_price")}
                    >
                      ${product.price.toFixed(2)}
                    </td>

                    <td data-label={t("buttons.txt_in_stock")}>
                      <span
                        className={`product-stock-pill product-stock-${status}`}
                      >
                        <span className="product-stock-dot" />
                        {statusLabel}
                      </span>
                    </td>

                    <td data-label={t("buttons.txt_meta_dynamic")}>
                      <button
                        className="meta-link-button"
                        onClick={() => handleCopyMetaDynamicLink(product.id)}
                        title="Copy meta dynamic link"
                        aria-label="Copy meta dynamic link"
                      >
                        <FiFacebook size={16} />
                      </button>
                    </td>

                    <td
                      className="td-actions"
                      data-label={t("buttons.txt_actions")}
                    >
                      <IconButton
                        label="Edit product"
                        onClick={() => handleEditClick(product)}
                      >
                        <FiEdit2 size={17} />
                      </IconButton>
                      <IconButton
                        label="Delete product"
                        onClick={() => handleDeleteProductClick(product.id)}
                      >
                        <FiTrash2 size={17} color={COLORS.Palette.Red} />
                      </IconButton>
                      <IconButton
                        label="More details"
                        to={`/dashboard/products/${product.id}`}
                        as={Link}
                      >
                        <FiInfo size={17} />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {filteredProducts.length > 0 && (
          <div className="product-table-footer">
            Showing {filteredProducts.length} of {stats.total} products
          </div>
        )}
      </div>

      <Dialog
        onClick={handleDeleteProduct}
        open={openDialoge}
        onClose={() => setOpenDialoge(false)}
        title={t("popup.txt_delete_product")}
        description={t("popup.are_you_sure_delete_product")}
        positiveButtonText={t("popup.txt_delete")}
        negativeButtonText={t("popup.txt_cancel")}
      />
    </div>
  );
};
